import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type {
  DisciplineProgressReportsFilterParams,
  DisciplineProgressReportsFilters,
} from '../model/interfaces'

import dayjs from 'dayjs'

import { getFirstQueryValue } from '@shared/lib/routes'

const FILTER_QUERY_KEYS: readonly string[] = ['due_date_from', 'due_date_to']
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

/** A `YYYY-MM-DD HH:mm:ss` bound, or `null` for anything the API would answer with a 422. */
const toDateTimeValue = (value: unknown): string | null => {
  const raw = getFirstQueryValue(value)
  return raw !== null && DATE_TIME_PATTERN.test(raw) && dayjs(raw).isValid() ? raw : null
}

export const emptyDisciplineProgressReportsFilters = (): DisciplineProgressReportsFilters => ({
  dueDateFrom: null,
  dueDateTo: null,
})

export const queryToDisciplineProgressReportsFilters = (query: Record<string, unknown>): DisciplineProgressReportsFilters => {
  const dueDateFrom = toDateTimeValue(query.due_date_from)

  return {
    dueDateFrom,
    // The picker cannot show an end without a start, so a lone `to` would filter invisibly.
    dueDateTo: dueDateFrom ? toDateTimeValue(query.due_date_to) : null,
  }
}

export const getDisciplineProgressReportsFilterParams = (
  filters: DisciplineProgressReportsFilters,
): DisciplineProgressReportsFilterParams => ({
  due_date_from: filters.dueDateFrom || undefined,
  due_date_to: filters.dueDateTo || undefined,
})

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const disciplineProgressReportsFiltersToQuery = (filters: DisciplineProgressReportsFilters): LocationQueryRaw => ({
  ...getDisciplineProgressReportsFilterParams(filters),
})

/** The query rewritten the way `filters` reads it, or `null` when it already is. */
export const getCanonicalDisciplineProgressReportsQuery = (
  query: LocationQuery,
  filters: DisciplineProgressReportsFilters,
): LocationQueryRaw | null => {
  const canonical = disciplineProgressReportsFiltersToQuery(filters)
  const isCanonical = FILTER_QUERY_KEYS.every(key => (getFirstQueryValue(query[key]) ?? undefined) === canonical[key])

  return isCanonical ? null : { ...query, ...canonical }
}
